import { z } from 'zod';
import { FILE_ENTITY_TYPES } from './api.js';
export const emailSchema = z.string().trim().toLowerCase().email();
export const passwordSchema = z
    .string()
    .min(10, 'Password must be at least 10 characters')
    .max(200);
export const slugSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,62}$/, 'Use lowercase letters, numbers, hyphens and underscores only');
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
export const updateProfileSchema = z.object({
    name: z.string().trim().min(1).max(120),
    email: emailSchema,
});
// Roles a company admin may hand out from the user screen. super_admin is
// created once by the installer, and guests come through the guest flow
// because they need a validity period and a host department (§3.6).
export const INVITABLE_ROLES = ['company_admin', 'dept_admin', 'employee'];
export const inviteUserSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(1).max(120),
    role: z.enum(INVITABLE_ROLES),
});
/** Payload for the one-shot install wizard. */
export const setupSchema = z.object({
    setupToken: z.string().min(1),
    siteName: z.string().trim().min(1).max(120),
    adminName: z.string().trim().min(1).max(120),
    adminEmail: emailSchema,
    adminPassword: passwordSchema,
    locale: z.enum(['en', 'ko']).default('en'),
    timezone: z.string().default('America/New_York'),
});
export const uploadIntentSchema = z.object({
    entityType: z.enum(FILE_ENTITY_TYPES),
    originalName: z.string().trim().min(1).max(255),
    mimeType: z.string().trim().min(1).max(160),
    sizeBytes: z.number().int().positive(),
});
export const settingsPatchSchema = z.record(z.string(), z.unknown());
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * Setting a password: ask for a link, then redeem it to choose one (§4.6).
 *
 * The same pair covers a first password and a forgotten one. Holding the link
 * is what proves the address, so redeeming it is also the email verification.
 */
export const requestLinkSchema = z.object({
    email: emailSchema,
});
export const setPasswordSchema = z.object({
    token: z.string().min(20).max(200),
    password: z.string().min(10).max(200),
});
/** Second factor for the roles that can see the roster or the money (§4.2). */
export const mfaVerifySchema = z.object({
    ticket: z.string().min(20).max(200),
    code: z.string().trim().min(6).max(20),
});
export const mfaEnableSchema = z.object({
    code: z.string().trim().min(6).max(20),
});
export const acceptInviteSchema = z.object({
    token: z.string().min(20).max(200),
    /** Required for roles that sign in with a password; ignored for the rest. */
    password: passwordSchema.optional(),
});
/* Organisation (§P2). Names are trimmed so " Line A" and "Line A" are one place. */
const trimmedName = z.string().trim().min(1).max(120);
export const siteSchema = z.object({
    name: trimmedName,
    kind: z.enum(['factory', 'office']).default('factory'),
    address: z.string().trim().max(300).optional(),
    // Rejected early rather than at cutoff time, where a bad zone silently shifts
    // every deadline (§3.5).
    timezone: z.string().trim().min(1).max(64).default('America/New_York'),
});
export const departmentSchema = z.object({
    name: trimmedName,
    siteId: z.string().uuid().optional(),
    costCenter: z.string().trim().max(60).optional(),
});
export const deliveryPointSchema = z.object({
    siteId: z.string().uuid(),
    name: trimmedName,
    note: z.string().trim().max(200).optional(),
    sortOrder: z.number().int().min(0).max(9999).default(0),
});
export const restaurantSchema = z.object({
    name: trimmedName,
    phone: z.string().trim().max(40).optional(),
    memo: z.string().trim().max(500).optional(),
    closedWeekdays: z.array(z.number().int().min(1).max(7)).max(7).default([]),
    siteIds: z.array(z.string().uuid()).default([]),
});
export const guestSchema = z.object({
    name: trimmedName,
    email: emailSchema,
    organization: z.string().trim().max(120).optional(),
    hostDepartmentId: z.string().uuid().optional(),
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    allowedMealTypes: z.array(z.enum(['breakfast', 'lunch', 'dinner'])).min(1).default(['lunch']),
});
/* Menus and schedules (§P3). */
export const menuItemSchema = z.object({
    restaurantId: z.string().uuid(),
    name: trimmedName,
    description: z.string().trim().max(500).optional(),
    price: z.number().nonnegative().max(100000),
    currency: z.string().trim().length(3).default('USD'),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
    allergens: z.array(z.string().trim().max(40)).max(20).default([]),
    photoFileId: z.string().uuid().optional(),
});
/**
 * Changing a dish. Every field optional: a kitchen correcting a price should
 * not have to resend its allergens, and a field left out is a field left
 * alone rather than a field cleared.
 *
 * The restaurant is not among them. A dish does not move kitchens.
 */
export const menuItemPatchSchema = z
    .object({
    name: trimmedName.optional(),
    description: z.string().trim().max(500).optional(),
    price: z.number().nonnegative().max(100000).optional(),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']).optional(),
    allergens: z.array(z.string().trim().max(40)).max(20).optional(),
    isActive: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
export const scheduleSchema = z.object({
    restaurantId: z.string().uuid(),
    cycle: z.enum(['weekly', 'biweekly']).default('weekly'),
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const scheduleDaySchema = z.object({
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
    menuItemId: z.string().uuid(),
    capacity: z.number().int().positive().max(100000).optional(),
});
export const scheduleDaysSchema = z.object({
    /** Replaces the whole plan for this schedule in one call. */
    days: z.array(scheduleDaySchema).max(500),
});
export const cutoffSchema = z.object({
    restaurantId: z.string().uuid(),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
    /** Days before the service date. 1 = the evening before, the usual case. */
    offsetDays: z.number().int().min(0).max(14).default(1),
    cutoffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
/* Orders (§P4). */
export const placeOrderSchema = z.object({
    menuItemId: z.string().uuid(),
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
    deliveryPointId: z.string().uuid().optional(),
});
/**
 * Several meals in one pass, for people who book a week at a time.
 *
 * Capped at a fortnight of three meals: the menu never reaches further than
 * that, so anything larger is a client fault or somebody probing.
 */
export const placeOrdersSchema = z.object({
    items: z.array(placeOrderSchema).min(1).max(42),
});
export const manualOrderSchema = z.object({
    restaurantId: z.string().uuid(),
    menuItemId: z.string().uuid().optional(),
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
    quantity: z.number().int().positive().max(500),
    unitPrice: z.number().nonnegative().max(100000),
    /** The only account of why this exists, so it is required (§3.7). */
    note: z.string().trim().min(3).max(500),
    costDepartmentId: z.string().uuid().optional(),
});
/* Notices (§3 공지·알림). */
export const noticeSchema = z.object({
    title: trimmedName,
    body: z.string().trim().min(1).max(1000),
    /** Empty means every site. */
    siteIds: z.array(z.string().uuid()).max(50).default([]),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
});
/* Restaurant records of extra deliveries, and the company's review (§3.7). */
export const orderClaimSchema = z.object({
    /** Which company the food went to. Required where a kitchen serves several. */
    companyOrgId: z.string().uuid().optional(),
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
    menuItemId: z.string().uuid().optional(),
    quantity: z.number().int().positive().max(500),
    unitPrice: z.number().nonnegative().max(100000),
    /** What was delivered and who asked for it — the basis of any later dispute. */
    note: z.string().trim().min(1).max(500),
});
export const claimReviewSchema = z.object({
    note: z.string().trim().max(500).optional(),
});
export const markDeliveredSchema = z.object({
    restaurantId: z.string().uuid(),
    /** Which company's crate. A kitchen delivers to several in one morning. */
    companyOrgId: z.string().uuid().optional(),
    serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mealType: z.enum(['breakfast', 'lunch', 'dinner']),
});
export const orderOverrideSchema = z.object({
    /** Required: an override is only defensible if the reason is on the record. */
    reason: z.string().trim().min(1).max(500),
});
/* Roster import (§P2). Parsed in the browser so the admin sees it first. */
export const rosterRowSchema = z.object({
    name: trimmedName,
    email: z.string().trim().toLowerCase().email(),
    employeeNo: z.string().trim().max(40).optional(),
    departmentName: z.string().trim().max(120).optional(),
    deliveryPointName: z.string().trim().max(120).optional(),
});
export const rosterImportSchema = z.object({
    rows: z.array(rosterRowSchema).min(1).max(2000),
});
/**
 * How a meal's cost splits between company and employee (§3.4).
 *
 * Validated here rather than left to the generic settings bag: an unrecognised
 * shape falls back to "the company pays", and a typo that silently stops
 * charging people is exactly the kind of thing nobody notices for a month.
 */
export const subsidyPolicySchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('full') }),
    z.object({ kind: z.literal('capped'), companyMax: z.number().nonnegative().max(100000) }),
    z.object({ kind: z.literal('percent'), companyPercent: z.number().min(0).max(100) }),
]);
/**
 * How long data is kept (§11 item 5).
 *
 * `deleteOrders` defaults to false and stays false unless somebody says
 * otherwise: orders are financial records, and a company that gets audited
 * four years back would rather still have them.
 */
export const retentionPolicySchema = z.object({
    piiMonthsAfterLeaving: z.number().int().min(1).max(120),
    orderYears: z.number().int().min(1).max(30),
    deleteOrders: z.boolean().default(false),
});
/**
 * Signing an organisation up on a marketplace (§TENANCY 3).
 *
 * Only where both sides join independently. Where one organisation owns the
 * installation there is nothing to sign up to — the setup wizard made it.
 */
export const signupSchema = z.object({
    kind: z.enum(['company', 'restaurant']),
    orgName: trimmedName,
    adminName: z.string().trim().min(1).max(120),
    adminEmail: emailSchema,
    adminPassword: passwordSchema,
    phone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(200).optional(),
});
/**
 * Changing a subscription's terms (docs/TENANCY.md §10).
 *
 * Both fields are optional so a screen can send only what its switch changed,
 * but an empty body is refused: a PATCH that asks for nothing is a bug in the
 * caller, and silently answering "done" hides it.
 */
export const billingUpdateSchema = z
    .object({
    adsAllowed: z.boolean().optional(),
    term: z.enum(['monthly', 'annual']).optional(),
})
    .refine((body) => body.adsAllowed !== undefined || body.term !== undefined, {
    message: 'Nothing to change',
});
/**
 * Joining a company from its link (docs/TENANCY.md §11).
 *
 * The employee number is optional because not everybody on a site has one —
 * contractors, agency staff, the first week of a new hire. Without it the
 * person cannot be matched to the company's roster, so they go to whatever the
 * company decided for people it has not listed.
 *
 * No role field. The role is decided on the server, and it is always
 * `employee`: a link that could be asked for a role would be a way to mint
 * administrators from a poster on the canteen wall.
 */
export const joinSchema = z.object({
    employeeNo: z.string().trim().max(40).optional(),
    email: emailSchema,
    name: trimmedName,
    password: passwordSchema,
    pointId: z.string().uuid().optional(),
});
/** What a company decides about its link. */
export const joinLinkUpdateSchema = z
    .object({
    enabled: z.boolean().optional(),
    // What happens to somebody who is not on the roster. People who are on it
    // were already entered by the company, so there is nothing to decide.
    newcomers: z.enum(['approval', 'open']).optional(),
})
    .refine((body) => body.enabled !== undefined || body.newcomers !== undefined, {
    message: 'Nothing to change',
});
//# sourceMappingURL=schemas.js.map